const _ = require("lodash");

/**
 * Constructor for the Throttler class, which stores a collection of throttled
 * functions that each take on argument, obj. The main usage pattern is:
 * 1. Call config for any name defining a family of throttled functions. The
 *    function to run and the delay are specified here.
 * 2. Call queue(name, obj) to queue up a run of the throttled function.
 *
 * See the documentation of other methods for how to customize this flow.
 *
 * The options object can have these keys, all optional.
 * - getNow: Function taking no arguments defaulting to Date.now. Returns a
 *   timestamp with the current time in milliseconds.
 * - granularity: Number giving the milliseconds between calls to
 *   checkForScheduledRuns to see if anything throttled should run. Defaults to
 *   100. You can also pass a falsy value to disable the interval.
 *
 * ---
 *
 * Was getting "Declaration emit for this file requires using private name".
 * This `type` annotation is a workaround. See: https://stackoverflow.com/questions/60009092/declaration-will-not-emit-due-to-private-name-usage
 * @type {unknown}
 */
const Throttler = function(options) {
	const {granularity = 100, getNow = Date.now} = options || {};

	if (granularity) {
		if (!_.isFinite(granularity) || granularity < 0) {
			throw new Error(`Throttler constructor must have a positive granularity`);
		}
	}
	if (!_.isFunction(getNow)) {
		throw new Error(`Throttler constructor must pass a function for getNow`);
	}

	this.getNow = getNow;
	this.configByName = {};
	this.dataByID = {};
	this.queuedIDs = new Set();

	if (granularity) {
		setInterval(() => this.checkForScheduledRuns(), granularity);
	}
};

/**
 * Configures throttled operations for certain data. This method should be
 * called once per value of name, and other methods can only be called with
 * that name after this one is.
 *
 * props is an object with the following keys.
 * - delay: Required number, giving milliseconds between runs.
 * - run: Required function, taking the object waiting to run and
 *   actually performing the run.
 * - noLeading: Optional boolean defaulting to false. If true, never run
 *   immediately after a queue call and wait at least the delay.
 * - getID: Optional. A function taking the object to run and returning the
 *   string ID of the object. Each ID runs on a different timer. If omitted,
 *   defaults to always returning 0.
 *
 * @param {string} name
 * @param {object} props
 */
Throttler.prototype.config = function(name, props) {
	if (this.configByName[name]) {
		return;
	}
	if (!_.isFinite(props.delay) || props.delay < 0) {
		throw new Error(
			`throttler.config(${name}, ...) must have a positive delay`
		);
	}
	if (!_.isFunction(props.run)) {
		throw new Error(
			`throttler.config(${name}, ...) must pass a function for run`
		);
	}
	this.configByName[name] = props;
};

/**
 * Internal helper. Returns whether the given full ID is scheduled to run.
 *
 * @param {string} id
 * @param {number} now
 * @returns {boolean}
 */
const isReadyToRun = function(throttler, id, now) {
	const d = throttler.dataByID[id];
	if (!d || !d.queued) {
		// Not queued.
		return false;
	}

	const {delay, noLeading} = throttler.configByName[d.name];
	if (d.lastRun && d.lastRun + delay >= now) {
		// Last run too recent.
		return false;
	}
	if (noLeading && d.queued + delay >= now) {
		// Queue time too recent in noLeading case.
		return false;
	}
	return true;
};

/**
 * Helper used as the setInterval callback. Can also be called manually.
 *
 */
Throttler.prototype.checkForScheduledRuns = function() {
	const now = this.getNow();
	const idsToRun = [];
	this.queuedIDs.forEach(id => {
		if (isReadyToRun(this, id, now)) {
			idsToRun.push(id);
		}
	});
	idsToRun.forEach(id => {
		const d = this.dataByID[id];
		this.runNow(d.name, d.obj);
	});
};

/**
 * Internal helper. Gets the ID used in dataByID for the given name, obj
 * pair. Makes use of config.getID for that name.
 *
 * @param {Throttler} throttler
 * @param {string} name
 * @param {object} obj
 */
const getFullID = function(throttler, name, obj) {
	const c = throttler.configByName[name];
	if (!c) {
		throw new Error(`Called throttler for name ${name} before config`);
	}
	const customID = c.getID ? c.getID(obj) : 0;
	return `${name}_${customID}`;
};

/**
 * Internal helper. Modifies an entry in dataByID according to the passed
 * function and initializes it if needed. The function will take the current
 * data and should mutate it.
 *
 * After the function runs, this also updates queuedIDs to reflect which
 * entries have a truey queued value.
 *
 * @param {string} id
 * @param {string} name
 * @param {function} f
 */
const modifyData = function(throttler, id, name, f) {
	const d = throttler.dataByID[id] || {
		name,
		obj: null,
		queued: 0,
		lastRun: 0,
	};
	throttler.dataByID[id] = d;
	f(d);
	if (d.queued) {
		throttler.queuedIDs.add(id);
	} else {
		throttler.queuedIDs.delete(id);
	}
};

/**
 * Returns whether the given name/object pair has a queued run.
 *
 * @param {string} name
 * @param {object} obj
 * @returns {boolean}
 */
Throttler.prototype.isQueued = function(name, obj) {
	const id = getFullID(this, name, obj);
	return this.queuedIDs.has(id);
};

/**
 * Run the throttled function immediately, also cancelling any delayed
 * runs. Used externally, it is equivalent to calling queue then flush.
 *
 * @param {string} name
 * @param {object} obj
 */
Throttler.prototype.runNow = function(name, obj) {
	const id = getFullID(this, name, obj);
	const c = this.configByName[name];
	if (!c) {
		throw new Error(`Called throttler for name ${name} before config`);
	}
	modifyData(this, id, name, d => {
		d.obj = obj;
		d.queued = 0;
		d.lastRun = this.getNow();
	});
	c.run(obj);
};

/**
 * Queue up a run for the given name/object.
 *
 * @param {string} name
 * @param {object} obj
 */
Throttler.prototype.queue = function(name, obj) {
	const id = getFullID(this, name, obj);
	modifyData(this, id, name, d => {
		d.obj = obj || d.obj;
		d.queued = d.queued || this.getNow();
	});
};

/**
 * Cancel a queued run for the given name/object.
 *
 * @param {string} name
 * @param {object} obj
 */
Throttler.prototype.cancel = function(name, obj) {
	const id = getFullID(this, name, obj);
	modifyData(this, id, name, d => {
		d.obj = obj || d.obj;
		d.queued = 0;
	});
};

/**
 * If a run is queued for the given name/object, run it immediately. Else do
 * nothing.
 *
 * @param {string} name
 * @param {object} obj
 */
Throttler.prototype.flush = function(name, obj) {
	const id = getFullID(this, name, obj);
	if (this.queuedIDs.has(id)) {
		this.runNow(name, obj);
	}
};

/**
 * Without queueing or modifying the queue state, update the object that
 * should be passed to run.
 *
 * @param {string} name
 * @param {object} obj
 */
Throttler.prototype.setObj = function(name, obj) {
	const id = getFullID(this, name, obj);
	modifyData(this, id, name, d => {
		d.obj = obj;
	});
};

/**
 * Immediately run everything queued right now.
 *
 */
Throttler.prototype.flushAll = function() {
	const idsToRun = Array.from(this.queuedIDs);
	idsToRun.forEach(id => {
		const d = this.dataByID[id];
		this.runNow(d.name, d.obj);
	});
};

module.exports = Throttler;
